import { getAvailableSlots, type AvailableSlot } from "./availability";
import type { Service } from "@/lib/entities";
import { addDays, addHours } from "date-fns";
import { startOfDay } from "./dates";

export type Conflict = { start: string; end: string };
export type Conflicts = Conflict[];

/**
 * Fields required to reason about a service's booking window and availability.
 * Narrower than the full {@link Service} so we can reuse the same logic for
 * in-progress form previews.
 */
export type ServiceBookingInput = Pick<
  Service,
  | "availableSlots"
  | "durationMin"
  | "minNoticeHours"
  | "bookingWindowDays"
  | "bufferBeforeMin"
  | "bufferAfterMin"
  | "lookBusyEnabled"
  | "lookBusyLimit"
>;

export interface ServiceBookingWindow {
  /** First date (inclusive, at start-of-day) that could contain a bookable slot. */
  minDate: Date;
  /** Last date (inclusive, at start-of-day) that could contain a bookable slot. */
  maxDate: Date;
}

/**
 * Returns the booking window bounds for a service.
 *
 * The window is a rolling window anchored at `now`:
 *   - `minDate` = start-of-day of `now + minNoticeHours`
 *   - `maxDate` = start-of-day of `now + minNoticeHours + bookingWindowDays`
 *
 * Because the window slides with `now`, a service never "expires" on its own;
 * `bookingWindowDays` simply caps how far into the future a client may book.
 */
export function getServiceBookingWindow(
  service: ServiceBookingInput,
  now: Date = new Date(),
): ServiceBookingWindow {
  const earliest = addHours(now, service.minNoticeHours);
  const latest = addDays(earliest, service.bookingWindowDays);
  return {
    minDate: startOfDay(earliest),
    maxDate: startOfDay(latest),
  };
}

/**
 * Returns dates within [from, to] (inclusive) that have no bookable slots
 * given the service's availability and existing conflicts.
 */
export function getDisabledDatesInRange(
  service: ServiceBookingInput,
  from: Date,
  to: Date,
  conflicts?: Conflicts,
  now: Date = new Date(),
): Date[] {
  const disabledDates: Date[] = [];
  if (from > to) return disabledDates;

  const cursor = startOfDay(from);
  const end = startOfDay(to);
  while (cursor <= end) {
    const slots = getAvailableTimeSlotsForDate(service, cursor, conflicts, now);
    if (slots.length === 0) {
      disabledDates.push(new Date(cursor));
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return disabledDates;
}

/**
 * Returns the first bookable date/time within the service window, or `null`
 * when no day in the window has availability.
 */
export function getFirstAvailableDateTime(
  service: ServiceBookingInput,
  conflicts?: Conflicts,
  now: Date = new Date(),
): Date | null {
  const { minDate, maxDate } = getServiceBookingWindow(service, now);
  const cursor = new Date(minDate);
  while (cursor <= maxDate) {
    const slots = getAvailableTimeSlotsForDate(service, cursor, conflicts, now);
    if (slots.length > 0) return slots[0];
    cursor.setDate(cursor.getDate() + 1);
  }
  return null;
}

/**
 * Returns the last bookable date/time within the service window, or `null`
 * when no day in the window has availability.
 */
export function getLastAvailableDateTime(
  service: ServiceBookingInput,
  conflicts?: Conflicts,
  now: Date = new Date(),
): Date | null {
  const { minDate, maxDate } = getServiceBookingWindow(service, now);
  const cursor = new Date(maxDate);
  while (cursor >= minDate) {
    const slots = getAvailableTimeSlotsForDate(service, cursor, conflicts, now);
    const lastSlot = slots.at(-1);
    if (lastSlot) return lastSlot;
    cursor.setDate(cursor.getDate() - 1);
  }
  return null;
}

/**
 * Get available time slots for a specific date from service availability.
 * Returns time slots in 15-minute intervals that fit within the available
 * slots, respecting minimum notice, buffers, and existing conflicts.
 */
export function getAvailableTimeSlotsForDate(
  service: ServiceBookingInput,
  selectedDate: Date,
  conflicts?: Conflicts,
  now: Date = new Date(),
): Date[] {
  const availability = getAvailableSlots(service.availableSlots);
  if (availability.length === 0) return [];

  const weekday = WEEKDAY_FROM_JS_DAY[selectedDate.getDay()];
  const slotsForDay = availability.filter((slot) => slot.weekday === weekday);
  if (slotsForDay.length === 0) return [];

  const durationMin = service.durationMin;
  const bufferBeforeMin = service.bufferBeforeMin ?? 0;
  const bufferAfterMin = service.bufferAfterMin ?? 0;
  const minNoticeHours = service.minNoticeHours;
  const minDateTime = new Date(now.getTime() + minNoticeHours * MS_PER_HOUR);

  // Slots must fit duration. We also require room for the *larger* of the
  // two buffers at the boundary (matches pre-existing behavior).
  const totalTimeMin = durationMin + Math.max(bufferBeforeMin, bufferAfterMin);
  const bookingDurationMs = totalTimeMin * MS_PER_MINUTE;

  const normalizedConflicts = normalizeConflicts(conflicts);
  const overlapsConflict = (startMs: number, endMs: number) =>
    normalizedConflicts.some((c) => startMs < c.end && endMs > c.start);

  // Generate all possible slots for the day based purely on schedule configuration
  let allPossibleTimes: Date[] = [];
  for (const slot of slotsForDay) {
    const [startHour, startMin] = slot.startTime.split(":").map(Number);
    const [endHour, endMin] = slot.endTime.split(":").map(Number);
    const slotStartMin = startHour * 60 + startMin;
    const slotEndMin = endHour * 60 + endMin;

    let cursorMin = slotStartMin;
    while (cursorMin + totalTimeMin <= slotEndMin + bufferAfterMin) {
      const slotTime = new Date(selectedDate);
      slotTime.setHours(Math.floor(cursorMin / 60), cursorMin % 60, 0, 0);
      allPossibleTimes.push(slotTime);
      cursorMin += SLOT_INTERVAL_MIN;
    }
  }

  // Deduplicate and sort (slots for the same day may overlap if misconfigured).
  allPossibleTimes = Array.from(
    new Set(allPossibleTimes.map((d) => d.getTime())),
  )
    .map((t) => new Date(t))
    .sort((a, b) => a.getTime() - b.getTime());

  // Apply look busy on the deterministic base schedule
  if (
    service.lookBusyEnabled &&
    service.lookBusyLimit &&
    allPossibleTimes.length > service.lookBusyLimit
  ) {
    const seed = selectedDate.getTime();
    allPossibleTimes = applyLookBusyLimit(
      allPossibleTimes,
      service.lookBusyLimit,
      seed,
    );
  }

  // Filter the chosen slots against time passing and conflicts
  const finalTimes: Date[] = [];
  for (const slotTime of allPossibleTimes) {
    if (slotTime >= minDateTime) {
      const slotStartMs = slotTime.getTime() - bufferBeforeMin * MS_PER_MINUTE;
      const slotEndMs = slotTime.getTime() + bookingDurationMs;
      if (!overlapsConflict(slotStartMs, slotEndMs)) {
        finalTimes.push(slotTime);
      }
    }
  }

  return finalTimes;
}

// Internal helpers --------------------------------------------------------

function lcg(seed: number) {
  let state = seed;
  return function () {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
}

function applyLookBusyLimit(
  times: Date[],
  limit: number,
  seed: number,
): Date[] {
  if (times.length <= limit) return times;
  const rng = lcg(seed);
  const shuffled = [...times];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, limit).sort((a, b) => a.getTime() - b.getTime());
}

const SLOT_INTERVAL_MIN = 15;
const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;

const WEEKDAY_FROM_JS_DAY: Record<number, AvailableSlot["weekday"]> = {
  0: "sunday",
  1: "monday",
  2: "tuesday",
  3: "wednesday",
  4: "thursday",
  5: "friday",
  6: "saturday",
};

function normalizeConflicts(
  conflicts: Conflicts | undefined,
): Array<{ start: number; end: number }> {
  if (!conflicts) return [];
  return conflicts
    .map((c) => ({
      start: new Date(c.start).getTime(),
      end: new Date(c.end).getTime(),
    }))
    .filter(
      (c) => !Number.isNaN(c.start) && !Number.isNaN(c.end) && c.end > c.start,
    );
}
